import { useState, useEffect } from 'react';
import { X, User, Mail, Tv, Settings, Trash2, Check, Shield, Loader2, Send, Camera, Link2, Unlink, Bug, ChevronDown, ChevronUp, Pencil, Save, Globe, Gamepad2, RefreshCw, Lock, Palette } from 'lucide-react';
import { authService, supabase, roleService, UserRole, bugReportService, sparksService } from '../lib/supabase';
import { AvatarUploadModal } from './AvatarUploadModal';
import { GUILD_THEMES, useGuildTheme, GuildTheme } from '../lib/guildTheme';
import { SwatchPair } from './GuildThemePicker';

// SVG icons for OAuth providers
const GoogleIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className || "size-5"}>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
);

const DiscordIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className || "size-5"} fill="#5865F2">
        <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.175 13.175 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
    </svg>
);

const TwitchIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className || "size-5"} fill="#9146FF">
        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
);



interface Identity {
    id: string;
    provider: string;
    identity_data?: {
        email?: string;
        full_name?: string;
        name?: string;
        preferred_username?: string;
    };
}

interface AccountSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function AccountSettingsModal({ isOpen, onClose }: AccountSettingsModalProps) {
    const [user, setUser] = useState<any>(null);
    const [twitchUsername, setTwitchUsername] = useState<string | null>(null);
    const [discordUsername, setDiscordUsername] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState<UserRole>('user');
    const [hasPendingApplication, setHasPendingApplication] = useState(false);

    // Session & Remember Me preference
    const [rememberMe, setRememberMe] = useState<boolean>(() => {
        return localStorage.getItem('deckstr_remember_me') !== 'false';
    });

    const handleToggleRememberMe = (checked: boolean) => {
        setRememberMe(checked);
        localStorage.setItem('deckstr_remember_me', checked ? 'true' : 'false');
    };

    // Theme preference state & handler
    const [currentGuildTheme, setCurrentGuildTheme] = useGuildTheme();
    const [savingTheme, setSavingTheme] = useState(false);
    const [themeSavedFeedback, setThemeSavedFeedback] = useState(false);

    const handleSelectGuildTheme = async (themeId: GuildTheme) => {
        setSavingTheme(true);
        try {
            await setCurrentGuildTheme(themeId);
            setThemeSavedFeedback(true);
            setTimeout(() => setThemeSavedFeedback(false), 2500);
        } catch (err) {
            console.error('Failed to save theme preference:', err);
        } finally {
            setSavingTheme(false);
        }
    };

    // Editable profile fields
    const [editingName, setEditingName] = useState(false);
    const [displayNameInput, setDisplayNameInput] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [editingEmail, setEditingEmail] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);
    const [emailMessage, setEmailMessage] = useState('');

    // Social links
    const [editingSocials, setEditingSocials] = useState(false);
    const [socialTwitter, setSocialTwitter] = useState('');
    const [socialYoutube, setSocialYoutube] = useState('');
    const [socialInstagram, setSocialInstagram] = useState('');
    const [savingSocials, setSavingSocials] = useState(false);

    // Gaming platform usernames
    const [editingGaming, setEditingGaming] = useState(false);
    const [arenaUsername, setArenaUsername] = useState('');
    const [mtgoUsername, setMtgoUsername] = useState('');
    const [savingGaming, setSavingGaming] = useState(false);

    // Mod application form
    const [showApplicationForm, setShowApplicationForm] = useState(false);
    const [applicationReason, setApplicationReason] = useState('');
    const [applicationLoading, setApplicationLoading] = useState(false);
    const [applicationSuccess, setApplicationSuccess] = useState(false);
    const [applicationError, setApplicationError] = useState('');

    // Avatar
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    // Connected accounts
    const [linkedIdentities, setLinkedIdentities] = useState<Identity[]>([]);
    const [verifyCode, setVerifyCode] = useState<string | null>(null);
    const [generatingCode, setGeneratingCode] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number>(600);
    const [codeExpired, setCodeExpired] = useState(false);
    const [verifySuccess, setVerifySuccess] = useState(false);

    // Live countdown timer for 2FA link code
    useEffect(() => {
        if (!verifyCode || verifySuccess || codeExpired) return;

        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setCodeExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [verifyCode, verifySuccess, codeExpired]);

    // Poll for verification completion when user types !verify in Twitch chat
    useEffect(() => {
        if (!verifyCode || !user || verifySuccess) return;

        const checkVerified = async () => {
            const siteUserId = `site_${user.id}`;
            const { data } = await supabase
                .from('user_links')
                .select('verified, twitch_display_name')
                .eq('discord_user_id', siteUserId)
                .maybeSingle();

            if (data?.verified) {
                setVerifySuccess(true);
                setTwitchUsername(data.twitch_display_name);
            }
        };

        const poll = setInterval(checkVerified, 3000);
        return () => clearInterval(poll);
    }, [verifyCode, user, verifySuccess]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // Manual streamer registration (workaround for broken OAuth linking)
    const [manualTwitchInput, setManualTwitchInput] = useState('');
    const [linkingProvider, setLinkingProvider] = useState<string | null>(null);

    // Bug report form
    const [showBugForm, setShowBugForm] = useState(false);
    const [bugArea, setBugArea] = useState('');
    const [bugSubArea, setBugSubArea] = useState('');
    const [bugDescription, setBugDescription] = useState('');
    const [bugNotes, setBugNotes] = useState('');
    const [bugLoading, setBugLoading] = useState(false);
    const [bugSuccess, setBugSuccess] = useState(false);
    const [bugError, setBugError] = useState('');

    // Predefined location options with sub-areas
    const BUG_LOCATIONS: Record<string, string[]> = {
        'Login / Account': ['Sign in popup', 'Sign up', 'OAuth (Google/Discord/Twitch)', 'Password reset', 'Account settings'],
        'Discover Tab': ['Deck list', 'Deck detail view', 'Streamer cards', 'Search/Filters', 'Voting/Views'],
        'Deck Builder': ['Card search', 'Adding/Removing cards', 'Sideboard', 'Lands', 'Saving deck'],
        'Deck Playtester': ['Drawing cards', 'Game zones', 'Controls', 'Mulligan'],
        'Session Tab': ['Player slots', 'P2P connection', 'Life counters'],
        'Events Tab': ['Event list', 'Registration', 'Creating events'],
        'Analytics / Stats': ['Mana curve', 'Probabilities', 'Charts'],
        'Header / Navigation': ['Menu', 'Tabs', 'User dropdown'],
        'Other': ['Visual/Styling', 'Performance', 'Something else'],
    };

    const handleSubmitBug = async () => {
        if (!bugArea || !bugDescription.trim()) {
            setBugError('Please select where the bug is and describe what happened');
            return;
        }

        setBugLoading(true);
        setBugError('');

        // Combine area + sub-area for location
        const fullLocation = bugSubArea ? `${bugArea} → ${bugSubArea}` : bugArea;

        try {
            await bugReportService.submitBugReport({
                location: fullLocation,
                description: bugDescription.trim(),
                expected: bugNotes.trim() || undefined,
            });
            setBugSuccess(true);
            setBugArea('');
            setBugSubArea('');
            setBugDescription('');
            setBugNotes('');
            setTimeout(() => {
                setBugSuccess(false);
                setShowBugForm(false);
            }, 2000);
        } catch (err: any) {
            setBugError(err.message || 'Failed to submit bug report');
        } finally {
            setBugLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadUserData();
        }
    }, [isOpen]);

    // Detect OAuth return and refresh data
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const linkedProvider = urlParams.get('provider_linked');
        if (linkedProvider) {
            console.log(`✅ Returned from ${linkedProvider} OAuth, refreshing session...`);
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
            // Force refresh the session to get updated identities
            supabase.auth.refreshSession().then(() => {
                console.log('✅ Session refreshed, loading user data...');
                loadUserData();
            });
            setLinkingProvider(null);
        }
    }, []);

    const loadUserData = async () => {
        // Get fresh user data
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        console.log('👤 Fresh user data:', freshUser);
        console.log('🔗 User identities:', freshUser?.identities);
        setUser(freshUser);

        if (freshUser) {
            // Load linked identities directly from user object
            const identities = freshUser.identities || [];
            console.log('📋 Setting linked identities:', identities);
            setLinkedIdentities(identities as Identity[]);

            // Check if registered as streamer or linked in user_links
            try {
                const { data: linkData } = await supabase
                    .from('user_links')
                    .select('twitch_display_name, discord_username, verified')
                    .eq('discord_user_id', `site_${freshUser.id}`)
                    .maybeSingle();

                if (linkData?.verified) {
                    if (linkData.twitch_display_name) setTwitchUsername(linkData.twitch_display_name);
                    if (linkData.discord_username) setDiscordUsername(linkData.discord_username);
                }
                
                if (!twitchUsername) {
                    const { data } = await supabase
                        .from('streamers')
                        .select('username')
                        .eq('user_id', freshUser.id)
                        .maybeSingle();

                    if (data) setTwitchUsername(data.username);
                }
            } catch {
                /* OK */
            }

            // Get user role
            const role = await roleService.getCurrentUserRole();
            setUserRole(role);

            // Check for pending application
            const pending = await roleService.hasPendingApplication();
            setHasPendingApplication(pending);

            // Load avatar from user metadata
            const avatar = freshUser.user_metadata?.avatar_url;
            if (avatar) setAvatarUrl(avatar);

            // Load display name and socials from user metadata
            setDisplayNameInput(freshUser.user_metadata?.display_name || '');
            setEmailInput(freshUser.email || '');
            setSocialTwitter(freshUser.user_metadata?.social_twitter || '');
            setSocialYoutube(freshUser.user_metadata?.social_youtube || '');
            setSocialInstagram(freshUser.user_metadata?.social_instagram || '');
            setArenaUsername(freshUser.user_metadata?.mtg_arena_username || '');
            setMtgoUsername(freshUser.user_metadata?.mtgo_username || '');
        }
    };

    const handleSaveDisplayName = async () => {
        if (!displayNameInput.trim()) return;
        setSavingName(true);
        try {
            const { error } = await supabase.auth.updateUser({
                data: { display_name: displayNameInput.trim() }
            });
            if (error) throw error;
            setUser((prev: any) => ({
                ...prev,
                user_metadata: { ...prev.user_metadata, display_name: displayNameInput.trim() }
            }));
            setEditingName(false);
        } catch (err: any) {
            alert(err.message || 'Failed to update display name');
        } finally {
            setSavingName(false);
        }
    };

    const handleSaveEmail = async () => {
        if (!emailInput.trim() || emailInput === user?.email) return;
        setSavingEmail(true);
        setEmailMessage('');
        try {
            const { error } = await supabase.auth.updateUser({ email: emailInput.trim() });
            if (error) throw error;
            setEmailMessage('Confirmation sent to your new email. Check your inbox!');
            setEditingEmail(false);
        } catch (err: any) {
            setEmailMessage(err.message || 'Failed to update email');
        } finally {
            setSavingEmail(false);
        }
    };

    const handleSaveSocials = async () => {
        setSavingSocials(true);
        try {
            const { error } = await supabase.auth.updateUser({
                data: {
                    social_twitter: socialTwitter.trim(),
                    social_youtube: socialYoutube.trim(),
                    social_instagram: socialInstagram.trim(),
                }
            });
            if (error) throw error;
            setEditingSocials(false);
        } catch (err: any) {
            alert(err.message || 'Failed to save socials');
        } finally {
            setSavingSocials(false);
        }
    };

    const handleSaveGaming = async () => {
        setSavingGaming(true);
        try {
            const { error } = await supabase.auth.updateUser({
                data: {
                    mtg_arena_username: arenaUsername.trim(),
                    mtgo_username: mtgoUsername.trim(),
                }
            });
            if (error) throw error;
            setEditingGaming(false);
        } catch (err: any) {
            alert(err.message || 'Failed to save gaming usernames');
        } finally {
            setSavingGaming(false);
        }
    };

    const handleLinkProvider = async (provider: 'google' | 'discord' | 'twitch' | 'twitter') => {
        if (provider === 'twitch') {
            await handleGenerateVerifyCode();
            return;
        }

        if (provider === 'discord') {
            const handle = prompt('Enter your Discord username to link your profile (+10 Bonus Sparks):', discordUsername || '');
            if (handle && handle.trim()) {
                const cleaned = handle.trim().replace(/^@/, '');
                setLoading(true);
                try {
                    const siteUserId = `site_${user?.id}`;
                    const { data: existing } = await supabase.from('user_links').select('id').eq('discord_user_id', siteUserId).maybeSingle();
                    if (existing) {
                        await supabase.from('user_links').update({ discord_username: cleaned, verified: true }).eq('id', existing.id);
                    } else {
                        await supabase.from('user_links').insert({ discord_user_id: siteUserId, discord_username: cleaned, twitch_user_id: `pending_${user?.id.slice(0, 8)}`, verified: true });
                    }
                    setDiscordUsername(cleaned);
                    alert(`✅ Discord account @${cleaned} connected! Your profile and Sparks are synced across deckstr.gg! ⚡`);
                } catch (e: any) {
                    alert('Failed to connect Discord: ' + (e.message || 'Unknown error'));
                } finally {
                    setLoading(false);
                }
            }
            return;
        }

        setLinkingProvider(provider);
        try {
            await authService.linkOAuthProvider(provider);
            // Will redirect - reload will happen on return
        } catch (err: any) {
            console.error(`Failed to link ${provider}:`, err);
            alert(`Failed to connect ${provider}: ${err.message || 'Unknown error'}`);
            setLinkingProvider(null);
        }
    };

    const handleUnlinkProvider = async (identityId: string, provider: string) => {
        if (!confirm(`Disconnect your ${provider} account? You can reconnect it anytime.`)) return;

        setLoading(true);
        try {
            await authService.unlinkIdentity(identityId);
            setLinkedIdentities(prev => prev.filter(i => i.id !== identityId));
        } catch (err: any) {
            console.error('Failed to unlink:', err);
            alert(err.message || 'Failed to disconnect account');
        } finally {
            setLoading(false);
        }
    };

    const handleLinkTwitch = async () => {
        await handleLinkProvider('twitch');
    };

    const handleUnlinkTwitch = async () => {
        if (!user || !confirm('Remove your Twitch registration? You will no longer appear in Discover Streamers.')) return;

        setLoading(true);
        try {
            await supabase.from('streamers').delete().eq('user_id', user.id);
            setTwitchUsername(null);
        } catch (err) {
            console.error('Failed to unlink Twitch:', err);
        } finally {
            setLoading(false);
        }
    };

    // Manual Twitch account sync + streamer registration
    const handleManualRegister = async () => {
        if (!user || !manualTwitchInput.trim()) return;

        const username = manualTwitchInput.trim().replace(/^@/, '').toLowerCase();

        setLoading(true);
        try {
            // 1. Sync to user_links so site Sparks + Twitch chat !sparks link instantly
            await supabase.from('user_links').upsert({
                supabase_user_id: user.id,
                twitch_user_id: username,
                twitch_display_name: username,
                discord_user_id: `site_${user.id}`,
                verified: true,
                verified_at: new Date().toISOString()
            }, { onConflict: 'twitch_user_id' });

            // 2. Register in streamers table
            await supabase.from('streamers').upsert({
                user_id: user.id,
                username: username,
                display_name: username,
                twitch_url: `https://twitch.tv/${username}`,
                is_live: false,
                viewer_count: 0
            }, { onConflict: 'user_id' });

            // 3. Award +10 signup sparks bonus
            await sparksService.awardSignupBonus();

            setTwitchUsername(username);
            setManualTwitchInput('');
            alert(`✅ Twitch account @${username} linked! Your Sparks balance is now synced across deckstr.gg and Twitch chat.`);
        } catch (err: any) {
            console.error('Failed to sync Twitch account:', err);
            alert(err.message || 'Failed to sync Twitch account');
        } finally {
            setLoading(false);
        }
    };

    // Generate 6-digit 2FA Verification Code for secure Twitch ownership proof (!verify)
    const handleGenerateVerifyCode = async () => {
        if (!user) return;
        setGeneratingCode(true);
        try {
            const code = String(Math.floor(100000 + Math.random() * 900000));
            const expires = new Date(Date.now() + 10 * 60 * 1000).toISOString();

            const siteUserId = `site_${user.id}`;

            // Check if user_links row exists for this site user
            const { data: existing } = await supabase
                .from('user_links')
                .select('id')
                .eq('discord_user_id', siteUserId)
                .maybeSingle();

            if (existing) {
                const { error: updateErr } = await supabase.from('user_links').update({
                    link_code: code,
                    link_code_expires: expires,
                    verified: false,
                }).eq('id', existing.id);
                if (updateErr) throw updateErr;
            } else {
                const uniqueId = `pending_${user.id.slice(0, 8)}_${Date.now()}`;
                const { error: insertErr } = await supabase.from('user_links').insert({
                    twitch_user_id: uniqueId,
                    discord_user_id: siteUserId,
                    link_code: code,
                    link_code_expires: expires,
                    verified: false,
                });
                if (insertErr) throw insertErr;
            }

            setVerifyCode(code);
            setTimeLeft(600);
            setCodeExpired(false);
            setVerifySuccess(false);
        } catch (err: any) {
            console.error('Failed to generate code:', err);
            alert('Failed to generate verification code: ' + (err.message || 'Unknown error'));
        } finally {
            setGeneratingCode(false);
        }
    };

    const handleSubmitApplication = async () => {
        if (!applicationReason.trim()) {
            setApplicationError('Please tell us why you want to be a mod');
            return;
        }

        setApplicationLoading(true);
        setApplicationError('');

        try {
            await roleService.submitModApplication(applicationReason.trim());
            setApplicationSuccess(true);
            setHasPendingApplication(true);
            setShowApplicationForm(false);
            setApplicationReason('');
        } catch (err: any) {
            setApplicationError(err.message || 'Failed to submit application');
        } finally {
            setApplicationLoading(false);
        }
    };

    if (!isOpen || !user) return null;

    const canApplyForMod = userRole === 'user' || userRole === 'member';

    return (
        <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
                <div className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/40 hover:text-white/60"
                    >
                        <X className="size-5" />
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <Settings className="size-6 text-cyan-400" />
                        <h2 className="text-xl font-bold text-white">Account Settings</h2>
                    </div>

                    {/* User Info */}
                    <div className="space-y-4">
                        <div className="bg-white/5 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-white/60 mb-3">Profile</h3>
                            <div className="flex gap-4">
                                {/* Clickable Avatar */}
                                <button
                                    onClick={() => setShowAvatarModal(true)}
                                    className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-white/20 hover:border-cyan-500/50 transition-colors group flex-shrink-0"
                                    title="Change profile picture"
                                >
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                                            <User className="size-8 text-white/30" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Camera className="size-5 text-white" />
                                    </div>
                                </button>

                                <div className="space-y-2 flex-1">
                                    {/* Display Name — editable */}
                                    <div className="flex items-center gap-2">
                                        <User className="size-4 text-white/40 flex-shrink-0" />
                                        {editingName ? (
                                            <div className="flex items-center gap-2 flex-1">
                                                <input
                                                    type="text"
                                                    value={displayNameInput}
                                                    onChange={(e) => setDisplayNameInput(e.target.value)}
                                                    maxLength={30}
                                                    autoFocus
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveDisplayName()}
                                                    className="flex-1 px-2 py-1 bg-black/30 border border-cyan-500/40 rounded text-white text-sm focus:outline-none focus:border-cyan-400"
                                                />
                                                <button
                                                    onClick={handleSaveDisplayName}
                                                    disabled={savingName || !displayNameInput.trim()}
                                                    className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                                >
                                                    {savingName ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                                                </button>
                                                <button
                                                    onClick={() => { setEditingName(false); setDisplayNameInput(user.user_metadata?.display_name || ''); }}
                                                    className="p-1 text-white/40 hover:text-white/60 rounded transition-colors"
                                                >
                                                    <X className="size-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="text-white">
                                                    {user.user_metadata?.display_name || 'No display name'}
                                                </span>
                                                <button
                                                    onClick={() => setEditingName(true)}
                                                    className="p-1 text-white/30 hover:text-cyan-400 rounded transition-colors"
                                                    title="Edit display name"
                                                >
                                                    <Pencil className="size-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Email — editable */}
                                    <div className="flex items-center gap-2">
                                        <Mail className="size-4 text-white/40 flex-shrink-0" />
                                        {editingEmail ? (
                                            <div className="flex items-center gap-2 flex-1">
                                                <input
                                                    type="email"
                                                    value={emailInput}
                                                    onChange={(e) => setEmailInput(e.target.value)}
                                                    autoFocus
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEmail()}
                                                    className="flex-1 px-2 py-1 bg-black/30 border border-cyan-500/40 rounded text-white text-sm focus:outline-none focus:border-cyan-400"
                                                />
                                                <button
                                                    onClick={handleSaveEmail}
                                                    disabled={savingEmail || !emailInput.trim() || emailInput === user.email}
                                                    className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                                >
                                                    {savingEmail ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                                                </button>
                                                <button
                                                    onClick={() => { setEditingEmail(false); setEmailInput(user.email || ''); setEmailMessage(''); }}
                                                    className="p-1 text-white/40 hover:text-white/60 rounded transition-colors"
                                                >
                                                    <X className="size-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="text-white/70 text-sm">{user.email}</span>
                                                <button
                                                    onClick={() => setEditingEmail(true)}
                                                    className="p-1 text-white/30 hover:text-cyan-400 rounded transition-colors"
                                                    title="Change email"
                                                >
                                                    <Pencil className="size-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {emailMessage && (
                                        <p className={`text-xs ml-6 ${emailMessage.includes('Confirm') ? 'text-green-400' : 'text-amber-400'}`}>
                                            {emailMessage}
                                        </p>
                                    )}

                                    {/* Role badge */}
                                    <div className="flex items-center gap-3">
                                        <Shield className="size-4 text-white/40" />
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${userRole === 'admin' ? 'bg-red-500/20 text-red-400' :
                                            userRole === 'mod' ? 'bg-purple-500/20 text-purple-400' :
                                                userRole === 'member' ? 'bg-cyan-500/20 text-cyan-400' :
                                                    'bg-white/10 text-white/50'
                                            }`}>
                                            {userRole}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Session & Preferences */}
                        <div className="bg-white/5 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-white/60 mb-3">Session & Login Preferences</h3>
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => handleToggleRememberMe(e.target.checked)}
                                    className="w-4 h-4 rounded border-white/20 bg-slate-800 text-amber-500 focus:ring-amber-500/50 cursor-pointer"
                                />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-white group-hover:text-amber-400 transition-colors">
                                        Remember Me
                                    </span>
                                    <span className="text-xs text-white/40">
                                        Stay signed in on this device across browser sessions.
                                    </span>
                                </div>
                            </label>
                        </div>

                        {/* Theme Preferences */}
                        <div className="bg-white/5 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Palette className="size-4 text-amber-400" />
                                    <h3 className="text-sm font-medium text-white/60">Favorite Color Theme</h3>
                                </div>
                                {themeSavedFeedback && (
                                    <span className="text-xs text-green-400 flex items-center gap-1">
                                        <Check className="size-3.5" /> Saved to account
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-white/40 mb-3">
                                Select your favorite MTG Guild color scheme. Your theme preference is saved to your account and syncs across all your devices.
                            </p>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {GUILD_THEMES.map((t) => {
                                    const isSelected = currentGuildTheme === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => handleSelectGuildTheme(t.id)}
                                            className={`flex items-center gap-2.5 p-2 rounded-lg border text-left transition-all ${
                                                isSelected
                                                    ? 'bg-purple-500/20 border-amber-400/60 shadow-[0_0_12px_rgba(245,158,11,0.15)] ring-1 ring-amber-400/30'
                                                    : 'bg-black/20 border-white/5 hover:border-white/20 hover:bg-white/5'
                                            }`}
                                        >
                                            <SwatchPair swatches={t.swatches} size="size-3.5" />
                                            <div className="flex-1 min-w-0">
                                                <div className={`text-xs font-medium truncate ${isSelected ? 'text-amber-300' : 'text-white/80'}`}>
                                                    {t.name}
                                                </div>
                                                <div className="text-[10px] text-white/40 truncate">
                                                    {t.colors}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <Check className="size-3.5 text-amber-400 shrink-0" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="bg-white/5 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Globe className="size-4 text-cyan-400" />
                                    <h3 className="text-sm font-medium text-white/60">Social Links</h3>
                                </div>
                                {!editingSocials ? (
                                    <button
                                        onClick={() => setEditingSocials(true)}
                                        className="p-1 text-white/30 hover:text-cyan-400 rounded transition-colors"
                                        title="Edit socials"
                                    >
                                        <Pencil className="size-3.5" />
                                    </button>
                                ) : (
                                    <div className="flex gap-1">
                                        <button
                                            onClick={handleSaveSocials}
                                            disabled={savingSocials}
                                            className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                        >
                                            {savingSocials ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingSocials(false);
                                                setSocialTwitter(user.user_metadata?.social_twitter || '');
                                                setSocialYoutube(user.user_metadata?.social_youtube || '');
                                                setSocialInstagram(user.user_metadata?.social_instagram || '');
                                            }}
                                            className="p-1 text-white/40 hover:text-white/60 rounded transition-colors"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                {[
                                    { key: 'twitter', label: 'Twitter / X', placeholder: '@yourusername', value: socialTwitter, setter: setSocialTwitter },
                                    { key: 'youtube', label: 'YouTube', placeholder: 'channel name or URL', value: socialYoutube, setter: setSocialYoutube },
                                    { key: 'instagram', label: 'Instagram', placeholder: '@yourusername', value: socialInstagram, setter: setSocialInstagram },
                                ].map((social) => (
                                    <div key={social.key} className="flex items-center gap-3">
                                        <span className="text-xs text-white/40 w-20 flex-shrink-0">{social.label}</span>
                                        {editingSocials ? (
                                            <input
                                                type="text"
                                                value={social.value}
                                                onChange={(e) => social.setter(e.target.value)}
                                                placeholder={social.placeholder}
                                                className="flex-1 px-2 py-1 bg-black/30 border border-white/10 rounded text-white text-sm focus:outline-none focus:border-cyan-500/50"
                                            />
                                        ) : (
                                            <span className={`text-sm ${social.value ? 'text-white/70' : 'text-white/20 italic'}`}>
                                                {social.value || 'Not set'}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Gaming Platforms */}
                        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border border-emerald-500/20 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Gamepad2 className="size-4 text-emerald-400" />
                                    <h3 className="text-sm font-medium text-emerald-300">Gaming Platforms</h3>
                                </div>
                                {!editingGaming ? (
                                    <button
                                        onClick={() => setEditingGaming(true)}
                                        className="p-1 text-white/30 hover:text-emerald-400 rounded transition-colors"
                                        title="Edit gaming usernames"
                                    >
                                        <Pencil className="size-3.5" />
                                    </button>
                                ) : (
                                    <div className="flex gap-1">
                                        <button
                                            onClick={handleSaveGaming}
                                            disabled={savingGaming}
                                            className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                        >
                                            {savingGaming ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingGaming(false);
                                                setArenaUsername(user.user_metadata?.mtg_arena_username || '');
                                                setMtgoUsername(user.user_metadata?.mtgo_username || '');
                                            }}
                                            className="p-1 text-white/40 hover:text-white/60 rounded transition-colors"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-white/40 mb-3">
                                Your gaming usernames are shared when you join or host LFG games on these platforms.
                            </p>
                            <div className="space-y-2">
                                {[
                                    { key: 'arena', label: 'MTG Arena', placeholder: 'YourName#12345', value: arenaUsername, setter: setArenaUsername },
                                    { key: 'mtgo', label: 'MTGO', placeholder: 'YourMTGOName', value: mtgoUsername, setter: setMtgoUsername },
                                ].map((platform) => (
                                    <div key={platform.key} className="flex items-center gap-3">
                                        <span className="text-xs text-white/40 w-20 flex-shrink-0">{platform.label}</span>
                                        {editingGaming ? (
                                            <input
                                                type="text"
                                                value={platform.value}
                                                onChange={(e) => platform.setter(e.target.value)}
                                                placeholder={platform.placeholder}
                                                className="flex-1 px-2 py-1 bg-black/30 border border-white/10 rounded text-white text-sm focus:outline-none focus:border-emerald-500/50"
                                            />
                                        ) : (
                                            <span className={`text-sm ${platform.value ? 'text-white/70' : 'text-white/20 italic'}`}>
                                                {platform.value || 'Not set'}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mod Application - only show for users/members who aren't already mod/admin */}
                        {canApplyForMod && (
                            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Shield className="size-5 text-purple-400" />
                                    <h3 className="text-sm font-medium text-purple-300">Become a Moderator</h3>
                                </div>

                                {hasPendingApplication ? (
                                    <div className="text-center py-2">
                                        <p className="text-sm text-white/60">
                                            Your application is being reviewed.
                                        </p>
                                        <p className="text-xs text-white/40 mt-1">
                                            We'll notify you when there's an update.
                                        </p>
                                    </div>
                                ) : applicationSuccess ? (
                                    <div className="text-center py-2">
                                        <Check className="size-8 mx-auto text-green-400 mb-2" />
                                        <p className="text-sm text-green-400">Application submitted!</p>
                                    </div>
                                ) : showApplicationForm ? (
                                    <div className="space-y-3">
                                        <p className="text-xs text-white/50">
                                            Tell us why you'd like to help moderate DECKSTR.GG:
                                        </p>
                                        <textarea
                                            value={applicationReason}
                                            onChange={(e) => setApplicationReason(e.target.value)}
                                            placeholder="I want to help because..."
                                            rows={3}
                                            maxLength={500}
                                            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50 resize-none"
                                        />
                                        <p className="text-xs text-white/30">{applicationReason.length}/500</p>

                                        {applicationError && (
                                            <p className="text-xs text-red-400">{applicationError}</p>
                                        )}

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setShowApplicationForm(false)}
                                                className="flex-1 py-2 text-sm bg-white/10 text-white/60 rounded-lg hover:bg-white/20 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSubmitApplication}
                                                disabled={applicationLoading || !applicationReason.trim()}
                                                className="flex-1 py-2 text-sm bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                                            >
                                                {applicationLoading ? (
                                                    <Loader2 className="size-4 animate-spin" />
                                                ) : (
                                                    <Send className="size-4" />
                                                )}
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <p className="text-xs text-white/50 mb-3">
                                            Mods help keep DECKSTR.GG clean and welcoming. It's a trust-based role -
                                            you'll help review content and assist community members.
                                        </p>
                                        <button
                                            onClick={() => setShowApplicationForm(true)}
                                            className="w-full py-2 text-sm bg-purple-600/50 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Shield className="size-4" />
                                            Apply to be a Mod
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Already a mod/admin badge */}
                        {(userRole === 'mod' || userRole === 'admin') && (
                            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                    <Shield className="size-5 text-purple-400" />
                                    <span className="text-sm text-purple-300">
                                        You're a {userRole === 'admin' ? 'Site Admin' : 'Moderator'}
                                    </span>
                                    <Check className="size-4 text-green-400" />
                                </div>
                                <p className="text-xs text-white/40 mt-2">
                                    Access Admin Panel from the user menu to manage the community.
                                </p>
                            </div>
                        )}

                        {/* Connected Accounts */}
                        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Link2 className="size-5 text-cyan-400" />
                                <h3 className="text-sm font-medium text-cyan-300">Connected Accounts</h3>
                            </div>
                            <p className="text-xs text-white/50 mb-4">
                                Link multiple accounts to sign in with any of them. Accounts with the same verified email are automatically merged.
                            </p>

                            <div className="space-y-2">
                                {[
                                    { id: 'google' as const, name: 'Google', Icon: GoogleIcon, color: 'bg-white', textColor: 'text-gray-700' },
                                    { id: 'discord' as const, name: 'Discord', Icon: DiscordIcon, color: 'bg-[#5865F2]', textColor: 'text-white' },
                                    { id: 'twitch' as const, name: 'Twitch', Icon: TwitchIcon, color: 'bg-[#9146FF]', textColor: 'text-white' },
                                ].map((provider) => {
                                    const linkedIdentity = linkedIdentities.find(i => i.provider === provider.id);
                                    const isLinked = !!linkedIdentity || 
                                        (provider.id === 'twitch' && !!twitchUsername) ||
                                        (provider.id === 'discord' && !!discordUsername);
                                    const isLinking = linkingProvider === provider.id;
                                    const displayIdentifier = provider.id === 'twitch' && twitchUsername
                                        ? `@${twitchUsername}`
                                        : provider.id === 'discord' && discordUsername
                                            ? `@${discordUsername}`
                                            : (linkedIdentity?.identity_data?.email || linkedIdentity?.identity_data?.preferred_username || linkedIdentity?.identity_data?.name || 'Connected');

                                    return (
                                        <div
                                            key={provider.id}
                                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isLinked
                                                ? 'bg-green-500/10 border-green-500/30'
                                                : 'bg-white/5 border-white/10 hover:border-white/20'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full ${provider.color} flex items-center justify-center`}>
                                                    <provider.Icon className="size-4" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white text-sm font-medium">{provider.name}</span>
                                                        {isLinked && <Check className="size-3.5 text-green-400" />}
                                                    </div>
                                                    {isLinked && (
                                                        <p className="text-xs text-white/40">
                                                            {displayIdentifier}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {isLinked ? (
                                                <button
                                                    onClick={() => linkedIdentity ? handleUnlinkProvider(linkedIdentity.id, provider.name) : handleUnlinkTwitch()}
                                                    disabled={loading}
                                                    className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                                    title={`Disconnect ${provider.name}`}
                                                >
                                                    <Unlink className="size-4" />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleLinkProvider(provider.id)}
                                                    disabled={isLinking || loading}
                                                    className="px-3 py-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                                >
                                                    {isLinking ? (
                                                        <Loader2 className="size-3 animate-spin" />
                                                    ) : (
                                                        <Link2 className="size-3" />
                                                    )}
                                                    Connect
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Twitch Connection & Sparks Sync */}
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-purple-300 mb-1 flex items-center gap-2">
                                <Shield className="size-4 text-purple-400" />
                                Sync Twitch Account & Sparks ⚡
                            </h3>

                            {twitchUsername ? (
                                <div className="flex items-center justify-between mt-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                            <Tv className="size-4 text-purple-400" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium">@{twitchUsername}</span>
                                                <Check className="size-4 text-green-400" />
                                            </div>
                                            <p className="text-xs text-white/40">Verified via 2FA Twitch Proof</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleUnlinkTwitch}
                                        disabled={loading}
                                        className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                        title="Remove Twitch link"
                                    >
                                        <Trash2 className="size-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3 mt-2">
                                    <p className="text-white/60 text-xs">
                                        To prevent identity fraud, Twitch accounts must be verified using 2-Factor Chat Proof.
                                    </p>

                                    {verifySuccess ? (
                                        <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-3 text-center space-y-1">
                                            <Check className="size-6 text-green-400 mx-auto" />
                                            <h4 className="text-xs font-bold text-green-300">Account Verified & Linked!</h4>
                                            <p className="text-[11px] text-white/70">
                                                Your Twitch account <span className="font-semibold text-white">@{twitchUsername}</span> and site Sparks are now synced.
                                            </p>
                                        </div>
                                    ) : verifyCode ? (
                                        <div className="bg-purple-950/60 border border-purple-500/40 rounded-lg p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-purple-300 font-semibold uppercase tracking-wider flex items-center gap-1">
                                                    <Shield className="size-3 text-purple-400" />
                                                    2FA Verification Code
                                                </span>
                                                <span className={`text-xs font-mono font-bold ${codeExpired ? 'text-red-400 animate-pulse' : 'text-amber-400'}`}>
                                                    {codeExpired ? '⚠️ EXPIRED' : `Expires in ${formatTime(timeLeft)}`}
                                                </span>
                                            </div>

                                            {codeExpired ? (
                                                <div className="space-y-2 py-1 text-center">
                                                    <p className="text-xs text-red-300 font-medium">
                                                        Code has expired! Click below to generate a new verification code.
                                                    </p>
                                                    <button
                                                        onClick={handleGenerateVerifyCode}
                                                        disabled={generatingCode}
                                                        className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2"
                                                    >
                                                        {generatingCode ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
                                                        Refresh New Code
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-center py-2 bg-black/50 rounded border border-purple-500/30 flex items-center justify-between px-3">
                                                        <code className="text-2xl font-mono font-bold tracking-widest text-amber-300">
                                                            !verify {verifyCode}
                                                        </code>
                                                        <button
                                                            onClick={handleGenerateVerifyCode}
                                                            disabled={generatingCode}
                                                            title="Refresh new code"
                                                            className="p-1.5 text-white/40 hover:text-purple-300 rounded transition-colors"
                                                        >
                                                            <RefreshCw className={`size-4 ${generatingCode ? 'animate-spin' : ''}`} />
                                                        </button>
                                                    </div>
                                                    <p className="text-[11px] text-white/70 leading-relaxed">
                                                        Type <span className="text-amber-400 font-mono font-bold">!verify {verifyCode}</span> in Twitch chat OR <span className="text-cyan-400 font-mono font-bold">/verify code: {verifyCode}</span> in Discord <span className="text-indigo-300 font-semibold">#verify</span> to complete 2FA verification and claim +10 Bonus Sparks! ⚡
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <button
                                                onClick={handleGenerateVerifyCode}
                                                disabled={generatingCode}
                                                className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20"
                                            >
                                                {generatingCode ? <Loader2 className="size-4 animate-spin" /> : <Shield className="size-4" />}
                                                Get Secure 2FA Link Code (!verify)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Bug Report Section */}
                        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg p-4">
                            <button
                                onClick={() => setShowBugForm(!showBugForm)}
                                className="w-full flex items-center justify-between"
                            >
                                <div className="flex items-center gap-2">
                                    <Bug className="size-5 text-amber-400" />
                                    <h3 className="text-sm font-medium text-amber-300">Report a Bug</h3>
                                </div>
                                {showBugForm ? (
                                    <ChevronUp className="size-4 text-amber-400" />
                                ) : (
                                    <ChevronDown className="size-4 text-amber-400" />
                                )}
                            </button>

                            {showBugForm && (
                                <div className="mt-4 space-y-3">
                                    <p className="text-xs text-white/50">
                                        Found something broken? Let us know!
                                    </p>

                                    {bugSuccess ? (
                                        <div className="text-center py-4">
                                            <Check className="size-8 mx-auto text-green-400 mb-2" />
                                            <p className="text-sm text-green-400">Bug report submitted!</p>
                                            <p className="text-xs text-white/40 mt-1">Thanks for helping us improve.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Area dropdown */}
                                            <div>
                                                <label className="text-xs text-white/40 block mb-1">Where in the app?</label>
                                                <select
                                                    value={bugArea}
                                                    onChange={(e) => {
                                                        setBugArea(e.target.value);
                                                        setBugSubArea(''); // Reset sub-area when area changes
                                                    }}
                                                    className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50 appearance-none cursor-pointer"
                                                >
                                                    <option value="">Select an area...</option>
                                                    {Object.keys(BUG_LOCATIONS).map(area => (
                                                        <option key={area} value={area}>{area}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Sub-area dropdown (shown only if area is selected) */}
                                            {bugArea && BUG_LOCATIONS[bugArea]?.length > 0 && (
                                                <div>
                                                    <label className="text-xs text-white/40 block mb-1">Specific area (optional)</label>
                                                    <select
                                                        value={bugSubArea}
                                                        onChange={(e) => setBugSubArea(e.target.value)}
                                                        className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50 appearance-none cursor-pointer"
                                                    >
                                                        <option value="">General / Not sure</option>
                                                        {BUG_LOCATIONS[bugArea].map(sub => (
                                                            <option key={sub} value={sub}>{sub}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {/* Description */}
                                            <div>
                                                <label className="text-xs text-white/40 block mb-1">What happened?</label>
                                                <textarea
                                                    value={bugDescription}
                                                    onChange={(e) => setBugDescription(e.target.value)}
                                                    placeholder="Describe what went wrong..."
                                                    rows={3}
                                                    className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50 resize-none"
                                                />
                                            </div>

                                            {/* Additional notes */}
                                            <div>
                                                <label className="text-xs text-white/40 block mb-1">Additional notes (optional)</label>
                                                <input
                                                    type="text"
                                                    value={bugNotes}
                                                    onChange={(e) => setBugNotes(e.target.value)}
                                                    placeholder="Any other details, steps to reproduce, etc."
                                                    className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50"
                                                />
                                            </div>

                                            {bugError && (
                                                <p className="text-xs text-red-400">{bugError}</p>
                                            )}

                                            <button
                                                onClick={handleSubmitBug}
                                                disabled={bugLoading || !bugArea || !bugDescription.trim()}
                                                className="w-full py-2 text-sm bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                                            >
                                                {bugLoading ? (
                                                    <Loader2 className="size-4 animate-spin" />
                                                ) : (
                                                    <Send className="size-4" />
                                                )}
                                                Submit Bug Report
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Sign Out */}
                        <button
                            onClick={async () => {
                                await authService.signOut();
                                onClose();
                            }}
                            className="w-full py-3 bg-red-500/10 text-red-400 rounded-lg font-medium hover:bg-red-500/20 transition-colors"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>

            <AvatarUploadModal
                isOpen={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                currentAvatarUrl={avatarUrl}
                onAvatarUpdated={(newUrl) => {
                    setAvatarUrl(newUrl);
                    setShowAvatarModal(false);
                }}
            />
        </>
    );
}

